// components/IterativeLinearSolversExplanation.js
import React from 'react';

export default function IterativeLinearSolversExplanation() {
  return (
    <div className="w-full p-1 text-xs text-gray-800 text-justify leading-relaxed hyphens-auto md:text-sm">
      <p>
        <strong>Goal:</strong> Solve a system of linear equations <code>Ax = b</code>, where
        <code> A ∈ ℝⁿˣⁿ</code> is the coefficient matrix, <code>b ∈ ℝⁿ</code> is the right-hand side,
        and <code>x ∈ ℝⁿ</code> is the unknown we approximate iteratively.
      </p>

      <p className="mt-3"><strong>Mathematical explanation</strong></p>
      In the k'th iteration, given the current iterate <code>xₖ</code>:
      <ol className="list-decimal pl-5 space-y-2 mb-4">
        <li>
          <strong>Residual:</strong> Compute the mismatch <code>rₖ = b − A·xₖ</code>.
        </li>

        <li>
          <strong>Matrix splitting:</strong> Choose a splitting <code>A = M − N</code> so that solving
          linear systems with <code>M</code> is cheap (e.g., diagonal or triangular).
        </li>

        <li>
          <strong>Local linear solve (no explicit inverse):</strong> Compute the next iterate by solving
          the small system (we do <em>not</em> invert <code>M</code>):
          <pre className="bg-white p-2 border border-gray-200 rounded text-xs mb-1 flex items-center justify-center text-center min-h-[40px]">
            M · xₖ₊₁ = N · xₖ + b
          </pre>
          This is the practical form of the textbook update <code>xₖ₊₁ = M⁻¹(N·xₖ + b)</code>.
        </li>

        <li>
          <strong>Convergence check:</strong> stop when <code>‖rₖ₊₁‖ ≤ ε_abs + ε_rel · ‖b‖</code> or the
          maximum number of iterations is reached.
        </li>
      </ol>

      <p className="mt-3"><strong>Variants</strong></p>
      <ul className="list-disc pl-5 space-y-1 mb-4">
        <li>
          <strong>Richardson:</strong> residual correction with a constant step <code>α</code>.
          <pre className="bg-white p-2 border border-gray-200 rounded text-xs mb-1">
            xₖ₊₁ = xₖ + α · (b − A·xₖ)
          </pre>
        </li>

        <li>
          <strong>Jacobi:</strong> uses only the diagonal of <code>A</code>; all components update from <code>xₖ</code> (fully parallel).
          <pre className="bg-white p-2 border border-gray-200 rounded text-xs mb-1">
            M = diag(A),  N = M − A  <br/>
            →  M · xₖ₊₁ = N · xₖ + b
          </pre>
        </li>

        <li>
          <strong>Gauss–Seidel (forward / backward / symmetric):</strong> uses freshly updated values within a sweep.
          <pre className="bg-white p-2 border border-gray-200 rounded text-xs mb-1">
            Forward GS:   M = D − L,  N = U<br/>
            Backward GS:  M = D − U,  N = L<br/>
            Symmetric GS: FGS/BGS
          </pre>
        </li>

        <li>
          <strong>SSOR:</strong> symmetric Gauss–Seidel with relaxation <code>ω ∈ (0, 2)</code>; popular as a preconditioner.
        </li>

        <li>
          <strong>Conjugate Gradient (CG):</strong> for symmetric positive definite matrices; minimizes the quadratic
          energy in a growing Krylov subspace. Iteration (standard form):
          <pre className="bg-white p-2 border border-gray-200 rounded text-[11px] leading-5 mb-1 overflow-auto">
{`r₀ = b − A·x₀,  p₀ = r₀
for k = 0,1,2,...
  αₖ = (rₖᵀ rₖ) / (pₖᵀ A pₖ)
  xₖ₊₁ = xₖ + αₖ pₖ
  rₖ₊₁ = rₖ − αₖ A pₖ
  βₖ = (rₖ₊₁ᵀ rₖ₊₁) / (rₖᵀ rₖ)
  pₖ₊₁ = rₖ₊₁ + βₖ pₖ`}
          </pre>
        </li>

        <li>
          <strong>GMRES:</strong> for general (possibly nonsymmetric) matrices; minimizes <code>‖b − A·x‖₂</code>
          over a Krylov subspace via Arnoldi + small least squares.
          <pre className="bg-white p-2 border border-gray-200 rounded text-[11px] leading-5 mb-1 overflow-auto">
{`r₀ = b − A·x₀,  β = ‖r₀‖₂,  v₁ = r₀ / β
for j = 1..k:
  w = A vⱼ
  for i = 1..j:
    h_{i,j} = vᵢᵀ w
    w = w − h_{i,j} vᵢ
  h_{j+1,j} = ‖w‖₂,  v_{j+1} = w / h_{j+1,j}
Solve y = argmin_y ‖β e₁ − Ḣₖ y‖₂,   xₖ = x₀ + Vₖ y`}
          </pre>
          Use restarted GMRES(m) to bound memory by restarting after <code>m</code> steps.
        </li>
      </ul>

      <p className="mt-3"><strong>Input format</strong></p>
      <ul className="list-disc pl-5 space-y-1 mb-2">
        <li><strong>Matrix A:</strong> 2D Array.</li>
        <li><strong>Vector b:</strong> length-n right-hand side.</li>
        <li><strong>Initial guess x₀:</strong> optional (defaults to zero).</li>
        <li><strong>Method parameters:</strong> pick method; set tolerance <code>ε</code>, max iterations;
            optional <code>α</code> (Richardson) and <code>ω</code> (SOR/SSOR)..</li>
      </ul>

      <pre className="bg-white p-2 border border-gray-200 rounded text-xs mt-1 mb-2">
{`A: [[4, 1], [1, 3]]
b: [1, 2]
`}
      </pre>

      <p className="mt-3 font-semibold">Output format</p>
      <ul className="list-disc pl-5 mb-4">
        <li>Final solution vector <code>x</code>.</li>
        <li>Convergence status: "success" or "failure".</li>
        <li>Number of iterations.</li>
        <li>Final residual norm and a per-iteration residual history (plotted on a semilog scale).</li>
      </ul>
    </div>
  );
}
